import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { TextStyled } from '../Elements'
import { FooterLogo } from '../AllImages'
import { getQrImageSrc, QR_DOWNLOAD_URL } from '../../utils/qrDownload'

const WHATSAPP_URL = "https://wa.me/+966535316584"

const TopPromoBanner = () => {
    const { t, i18n } = useTranslation()
    const [isVisible, setIsVisible] = useState(false)

    useEffect(() => {
        // v2 key so previously-closed banners show again after layout fix
        const isClosed = localStorage.getItem('promo_banner_closed_v2')
        if (!isClosed) {
            setIsVisible(true)
        }
    }, [])

    const handleClose = (e) => {
        e.stopPropagation()
        setIsVisible(false)
        localStorage.setItem('promo_banner_closed_v2', 'true')
    }

    const handleBook = (e) => {
        e.stopPropagation()
        window.open(WHATSAPP_URL, '_blank')
    }

    if (!isVisible) return null

    const isRtl = i18n.language === 'ar'

    return (
        <BannerWrapper className={isRtl ? 'rtl' : 'ltr'}>
            <BannerInner>
                <AppIcon>
                    <img src={FooterLogo} alt="Sahab Al Alam" />
                </AppIcon>

                <MiddleSection>
                    <PromoText>{t('BannerPromoText')}</PromoText>
                    <StatsWrapper>
                        <Stars>
                            {"★★★★★".split('').map((star, i) => (
                                <span key={i} className="star">{star}</span>
                            ))}
                        </Stars>
                        <DownloadsText>{t('BannerDownloads')}</DownloadsText>
                    </StatsWrapper>
                </MiddleSection>

                <ActionSection>
                    <QRCodeWrapper onClick={(e) => e.stopPropagation()}>
                        <img
                            src={getQrImageSrc(QR_DOWNLOAD_URL, 300)}
                            srcSet={`${getQrImageSrc(QR_DOWNLOAD_URL, 300)} 1x, ${getQrImageSrc(QR_DOWNLOAD_URL, 600)} 2x`}
                            alt="Download QR Code"
                            loading="lazy"
                            width={40}
                            height={40}
                        />
                    </QRCodeWrapper>
                    <BookButton type="button" onClick={handleBook}>
                        {t('BannerBook')}
                    </BookButton>
                </ActionSection>

                <CloseButton type="button" onClick={handleClose} aria-label="Close banner">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                </CloseButton>
            </BannerInner>
        </BannerWrapper>
    )
}

const BannerWrapper = styled.div`
    z-index: 90;
    background: #002d40;
    color: var(--white);
    box-shadow: 0 4px 18px rgba(0, 0, 0, 0.18);

    /* Tablet + desktop: 65% width, side margin, below header */
    @media (min-width: 576px) {
        position: fixed;
        top: 78px;
        inset-inline-end: 16px;
        inset-inline-start: auto;
        width: 65%;
        max-width: calc(100% - 32px);
        border-radius: 14px;
        border: 1px solid rgba(255, 255, 255, 0.12);
    }

    @media (min-width: 992px) {
        top: 96px;
        inset-inline-end: 20px;
        max-width: calc(100% - 40px);
    }

    /* Phone only: full-width, below header */
    @media (max-width: 575px) {
        position: fixed;
        top: 78px;
        left: 0;
        right: 0;
        width: 100%;
        border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 0;
    }
`

const BannerInner = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 40px 12px 14px;
    position: relative;

    @media (max-width: 768px) {
        gap: 10px;
        padding: 10px 38px 10px 12px;
    }
`

const AppIcon = styled.div`
    width: 48px;
    height: 48px;
    flex-shrink: 0;
    border-radius: 10px;
    overflow: hidden;
    background: var(--white);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        display: block;
    }

    @media (max-width: 576px) {
        display: none;
    }
`

const MiddleSection = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 3px;
`

const PromoText = styled(TextStyled)`
    font-size: 14px;
    font-weight: 600;
    color: var(--white) !important;
    line-height: 1.35;
    margin: 0;
    position: relative;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;

    @media (max-width: 768px) {
        font-size: 11.5px;
        line-height: 1.3;
        -webkit-line-clamp: 2;
    }
`

const StatsWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`

const Stars = styled.div`
    display: flex;
    gap: 1px;

    .star {
        color: #ffc107;
        font-size: 11px;
        line-height: 1;
    }
`

const DownloadsText = styled(TextStyled)`
    font-size: 10px;
    font-weight: 700;
    color: rgba(255, 255, 255, 0.7) !important;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    margin: 0;
`

const ActionSection = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
`

const BookButton = styled.button`
    background-color: #ff3b5c;
    color: var(--white);
    border: none;
    padding: 9px 22px;
    min-width: 72px;
    font-size: 14px;
    font-weight: 700;
    border-radius: 10px;
    transition: all 0.2s ease;
    box-shadow: 0 4px 12px rgba(255, 59, 92, 0.25);
    white-space: nowrap;
    overflow: visible;
    flex-shrink: 0;

    &:hover {
        background-color: #ff244a;
        transform: translateY(-1px);
    }

    @media (max-width: 768px) {
        padding: 8px 16px;
        min-width: 64px;
        font-size: 13px;
    }
`

const QRCodeWrapper = styled.div`
    background: var(--white);
    padding: 3px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    img {
        width: 40px;
        height: 40px;
        display: block;
    }

    @media (max-width: 768px) {
        img {
            width: 34px;
            height: 34px;
        }
    }
`

const CloseButton = styled.button`
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    inset-inline-end: 10px;
    background: transparent;
    border: none;
    color: rgba(255, 255, 255, 0.55);
    padding: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: color 0.2s ease;

    svg {
        width: 16px;
        height: 16px;
    }

    &:hover {
        color: var(--white);
    }
`

export default TopPromoBanner
